import { useEffect } from 'react'

import { api } from '@ppwr/convex/_generated/api'
import { useMutation, useQuery } from 'convex/react'

import { create } from 'zustand'

import { useStore } from '@/models'

import { DS } from '@/design-system'



function useUser() {
  const {
    session: { user, isAdmin, isSuperAdmin },
    analytics: { setEvent }
  } = useStore()

  return {
    userId: user?.auth0Id,
    userName: user?.name,
    isAdmin,
    isSuperAdmin,
    setEvent
  }
}

function useHydrateAvatarPreferences() {
  const { preferences } = useQueryPreferences()
  const setSavedToQueryResults = useAvatarStore(s => s.setSavedToQueryResults)
  const setSavedToDefaults = useAvatarStore(s => s.setSavedToDefaults)
  const setSelectedToSaved = useAvatarStore(s => s.setSelectedToSaved)

  useEffect(() => {
    if (preferences === undefined) return   // still loading

    if (preferences) {
      setSavedToQueryResults(preferences)
    } else {
      setSavedToDefaults()
    }

    setSelectedToSaved()
    
  }, [preferences?.updatedAt])
}

function useQueryPreferences(){

  const { userId } = useUser()
  const preferences = useQuery(api.avatarPreferences.queries.getAvatarPreferences, userId ? { userId } : 'skip')
  return { preferences }

}

function useHandleResetForTesting() {

  const { userId } = useUser()

  const setSavedToDefaults = useAvatarStore(s => s.setSavedToDefaults)
  const setSelectedToDefaults = useAvatarStore(s => s.setSelectedToDefaults)
  const setError = useAvatarStore(s => s.setError)

  const resetAvatarPreferencesForTesting = useMutation(api.avatarPreferences.mutations.resetAvatarPreferencesForTesting)

  const handleResetForTesting = async () => {
    setError(null)
    try {
      if (!userId) {
        throw new Error()
      }
      await resetAvatarPreferencesForTesting({ userId })
      setSavedToDefaults()
      setSelectedToDefaults()
    } catch (err) {
      setError("Could not reset. Please try again.")
    }
  }

  return handleResetForTesting

}

function useHandleCancel(){

  const setError = useAvatarStore(s => s.setError)
  const setSelectedToSaved = useAvatarStore(s=>s.setSelectedToSaved)

  function handleCancel(){
    setError(null)
    setSelectedToSaved()
  }

  return handleCancel
}

//
function useHandleSave() {

    const { userId } = useUser()

    const selectedAvatarId = useAvatarStore(s => s.selectedAvatarId)
    const selectedBackgroundColor = useAvatarStore(s => s.selectedBackgroundColor)
    const selectedHairColor = useAvatarStore(s => s.selectedHairColor)
    const selectedSkinColor = useAvatarStore(s => s.selectedSkinColor)
    const selectedAccessoriesColor = useAvatarStore(s => s.selectedAccessoriesColor)

    const setSavedToSelected = useAvatarStore(s => s.setSavedToSelected)
    const setError = useAvatarStore(s => s.setError)

    const saveAvatarPreferences = useMutation(api.avatarPreferences.mutations.saveAvatarPreferences)

    const handleSave = async () => {
      setError(null)
      try {
        //if (Math.random() < 2) {
          //throw new Error("test")
        //}
        if (!userId) {
          throw new Error()
        }
        await saveAvatarPreferences({
          userId,
          avatarId: selectedAvatarId,
          backgroundColor: selectedBackgroundColor,
          hairColor: selectedHairColor,
          skinColor: selectedSkinColor,
          accessoriesColor: selectedAccessoriesColor
        })
        setSavedToSelected()
        return true
      } catch (err) {
        setError("Could not save. Please try again.")
        return false
      }
    }

    return handleSave

  }
//




type AvatarStore = {
    defaultAvatarId: string
    selectedAvatarId: string
    setSelectedAvatarId: (id: string) => void
    savedAvatarId: string | null
    setSavedAvatarId: (id: string) => void

    backgroundColors: string[]
    defaultBackgroundColor: string
    selectedBackgroundColor: string
    setSelectedBackgroundColor: (color: string) => void
    savedBackgroundColor: string | null
    setSavedBackgroundColor: (color: string) => void

    hairColors: string[]
    defaultHairColor: string
    selectedHairColor: string
    setSelectedHairColor: (color: string) => void
    savedHairColor: string | null
    setSavedHairColor: (color: string) => void

    skinColors: string[]
    defaultSkinColor: string
    selectedSkinColor: string
    setSelectedSkinColor: (color: string) => void
    savedSkinColor: string | null
    setSavedSkinColor: (color: string) => void

    accessoriesColors: string[]
    defaultAccessoriesColor: string
    selectedAccessoriesColor: string
    setSelectedAccessoriesColor: (color: string) => void
    savedAccessoriesColor: string | null
    setSavedAccessoriesColor: (color: string) => void

    setSavedToQueryResults: (prefs: {
      avatarId: string
      backgroundColor: string
      hairColor: string
      skinColor: string
      accessoriesColor?: string
    }) => void
    setSavedToDefaults: () => void
    setSelectedToSaved: () => void
    setSelectedToDefaults: () => void
    setSavedToSelected: () => void
    error: string | null
    setError: (err: string | null) => void
    init: () => void
  }

  const useAvatarStore = create<AvatarStore>((set,get)=>({
  defaultAvatarId: "Avatar0",
  selectedAvatarId: "Avatar0",
  setSelectedAvatarId: (id) => set((state) => ({ selectedAvatarId: id })),
  savedAvatarId: null,
  setSavedAvatarId: (id) => set((state) => ({ savedAvatarId: id })),

  backgroundColors: ['#812A50', "#1B6B4A", "#1E3A5F", "#4A2070", "#3D4F5F", "#2E2E38", "#1A5C5A", "#8B1A1A"],
  defaultBackgroundColor: DS.palette['Brand/Light Grey'],
  selectedBackgroundColor: DS.palette['Brand/Light Grey'],
  setSelectedBackgroundColor: (color) => set((state) => ({ selectedBackgroundColor: color })),
  savedBackgroundColor: null,
  setSavedBackgroundColor: (color) => set((state) => ({ savedBackgroundColor: color })),

  hairColors: ["#FFDD44", "#FF6B2B", "#C86D3B", "#381E19", "#121214", "#F0F4F8", "#00D2FF", "#E62E5C", "#FF52B2"],
  defaultHairColor: "white",
  selectedHairColor:  "white",
  setSelectedHairColor: (color) => set((state) => ({ selectedHairColor: color })),
  savedHairColor:  null,
  setSavedHairColor: (color) => set((state) => ({ savedHairColor: color })),

  skinColors: ["#FDF2E9", "#FFE9D6", "#FFD0A5", "#E1A67F", "#C57E61", "#AD7052", "#8C5A42", "#704730", "#593A2D"],
  defaultSkinColor: "white",
  selectedSkinColor:  "white",
  setSelectedSkinColor: (color) => set((state) => ({ selectedSkinColor: color })),
  savedSkinColor:  null,
  setSavedSkinColor: (color) => set((state) => ({ savedSkinColor: color })),

  accessoriesColors: ["#FFC72C", "#FF3B30", "#8E24AA", "#1E60FF", "#00C853", "#00E5FF", "#FF2A85", "#D1D5DB", "#2D3748"],
  defaultAccessoriesColor: "white",
  selectedAccessoriesColor:  "white",
  setSelectedAccessoriesColor: (color) => set((state) => ({ selectedAccessoriesColor: color })),
  savedAccessoriesColor:  null,
  setSavedAccessoriesColor: (color) => set((state) => ({ savedAccessoriesColor: color })),
  
  setSavedToQueryResults: function (obj){
    const {avatarId, backgroundColor, hairColor, skinColor, accessoriesColor} = obj;
    set((state) => ({
      savedAvatarId: avatarId,
      savedBackgroundColor: backgroundColor,
      savedHairColor: hairColor,
      savedSkinColor: skinColor,
      savedAccessoriesColor: accessoriesColor ?? state.defaultAccessoriesColor
    }))
  },
  setSavedToDefaults: function (){
    set((state) => ({ 
      savedAvatarId: state.defaultAvatarId,
      savedBackgroundColor: state.defaultBackgroundColor,
      savedHairColor: state.defaultHairColor,
      savedSkinColor: state.defaultSkinColor,
      savedAccessoriesColor: state.defaultAccessoriesColor
    }))
  },

  setSelectedToDefaults: function (){
    set((state) => ({ 
      selectedAvatarId: state.defaultAvatarId,
      selectedBackgroundColor: state.defaultBackgroundColor,
      selectedHairColor: state.defaultHairColor,
      selectedSkinColor: state.defaultSkinColor,
      selectedAccessoriesColor: state.defaultAccessoriesColor
    }))
  },
  setSelectedToSaved: function (){
    set((state) => ({
      selectedAvatarId: state.savedAvatarId ?? state.defaultAvatarId,
      selectedBackgroundColor: state.savedBackgroundColor ?? state.defaultBackgroundColor,
      selectedHairColor: state.savedHairColor ?? state.defaultHairColor,
      selectedSkinColor: state.savedSkinColor ?? state.defaultSkinColor,
      selectedAccessoriesColor: state.savedAccessoriesColor ?? state.defaultAccessoriesColor
    }))
  },
  setSavedToSelected: function (){
    set((state) => ({ 
      savedAvatarId: state.selectedAvatarId,
      savedBackgroundColor: state.selectedBackgroundColor,
      savedHairColor: state.selectedHairColor,
      savedSkinColor: state.selectedSkinColor,
      savedAccessoriesColor: state.selectedAccessoriesColor
    }))
  },
  error: null,
  setError: (err) => set((state) => ({ error: err })),
  init: function (){
    get().setError(null)
    get().setSavedToDefaults();
    get().setSelectedToSaved();
  }
}))

useAvatarStore.getState().init()

function AvatarPreferencesHydrator() {
  useHydrateAvatarPreferences()
  return null
}


export {
  useUser,
  useAvatarStore,
  useQueryPreferences,
  useHandleSave,
  useHandleCancel,
  useHandleResetForTesting,
  useHydrateAvatarPreferences,
  AvatarPreferencesHydrator
}
